import React, { useState, useEffect } from 'react';
import API_URL from '../settings';
import Autocomplete from '@mui/material/Autocomplete';
import { useAuth } from '../context/AuthContext';

import {
    Typography,
    Grid,
    TextField,
    Button,
    Box,
    Alert,
    Paper,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

function RecordForm() {
    const navigate = useNavigate();
    const { recordId } = useParams();
    const [searchParams] = useSearchParams();
    const presetPatientId = searchParams.get('patient_id');

    const { user } = useAuth();

    const [patients, setPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [adlGroup, setAdlGroup] = useState("-");

    const [formData, setFormData] = useState({
        patient_id: presetPatientId || '',
        checkup_date: new Date().toISOString().split('T')[0],
        blood_pressure: { systolic: '', diastolic: '' },
        blood_sugar: '',
        heart_rate: '',
        weight: '',
        height: '',
        notes: '',
        record_image: ''
    });

    const [status, setStatus] = useState({ type: '', message: '' });

    /* ===== STEP 2 : visit dialog ===== */
    const [openVisitDialog, setOpenVisitDialog] = useState(false);
    const [visitData, setVisitData] = useState({
        visit_date: '',
        visit_time: '09:00',
        location: 'บ้านผู้สูงอายุ',
        service: 'ติดตามสุขภาพ'
    });

    /* ---------- Load patients ---------- */
    useEffect(() => {
        const fetchData = async () => {
            try {
                const patientsUrl =
                    user.role === 'VOLUNTEER'
                        ? `${API_URL}/patients?volunteer_id=${user.id}`
                        : `${API_URL}/patients?hospital_id=${user.hospital_id}`;

                const res = await fetch(patientsUrl);
                if (!res.ok) throw new Error('โหลดข้อมูลไม่สำเร็จ');

                setPatients(await res.json());
            } catch (err) {
                setStatus({ type: 'error', message: err.message });
            }
        };

        fetchData();
    }, [user]);

    /* ---------- Sync preset patient & Load Record for Editing ---------- */
    useEffect(() => {
        if (patients.length === 0) return;

        const loadEditData = async () => {
            if (recordId) {
                try {
                    const res = await fetch(`${API_URL}/health_records/${recordId}`);
                    if (!res.ok) throw new Error('ไม่สามารถโหลดข้อมูลประวัติการตรวจได้');
                    const recordData = await res.json();
                    
                    const foundPatient = patients.find(p => String(p.id) === String(recordData.patient_id));
                    if (foundPatient) setSelectedPatient(foundPatient);

                    setFormData({
                        patient_id: recordData.patient_id,
                        checkup_date: recordData.checkup_date || '',
                        blood_pressure: {
                            systolic: recordData.blood_pressure?.systolic || '',
                            diastolic: recordData.blood_pressure?.diastolic || '',
                        },
                        blood_sugar: recordData.blood_sugar || '',
                        heart_rate: recordData.heart_rate || '',
                        weight: recordData.weight || '',
                        height: recordData.height || '',
                        notes: recordData.notes || '',
                        record_image: recordData.record_image || ''
                    });
                } catch (err) {
                    setStatus({ type: 'error', message: err.message });
                }
            } else if (presetPatientId) {
                const found = patients.find(
                    p => String(p.id) === String(presetPatientId)
                );
        
                if (found) {
                    setSelectedPatient(found);
                    setFormData(prev => ({
                        ...prev,
                        patient_id: found.id
                    }));
                }
            }
        };

        loadEditData();

    }, [recordId, presetPatientId, patients]);

    /* ---------- Fetch ADL Records ---------- */
    useEffect(() => {
        if (!selectedPatient) {
            setAdlGroup("-");
            return;
        }

        const fetchAdl = async () => {
            try {
                const res = await fetch(`${API_URL}/adl_records?patient_id=${selectedPatient.id}`);
                const data = await res.json();
                if (data && data.length > 0) {
                    const latest = data.sort((a, b) => new Date(b.assessment_date) - new Date(a.assessment_date))[0];
                    setAdlGroup(latest.adl_group || "ไม่มีข้อมูล");
                } else {
                    setAdlGroup("ไม่มีข้อมูล");
                }
            } catch (err) {
                console.error("Failed to fetch ADL records", err);
                setAdlGroup("-");
            }
        };

        fetchAdl();
    }, [selectedPatient]);

    /* ---------- Handlers ---------- */
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'systolic' || name === 'diastolic') {
            setFormData(prev => ({
                ...prev,
                blood_pressure: {
                    ...prev.blood_pressure,
                    [name]: value
                }
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({
                    ...prev,
                    record_image: reader.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const calculateBmi = (weight, height) => {
        if (!weight || !height) return null;
        const h = height / 100;
        return (weight / (h * h)).toFixed(2);
    };

    /* ---------- Submit health record ---------- */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({});

        if (!formData.patient_id) {
            setStatus({ type: 'error', message: 'กรุณาเลือกผู้สูงอายุ' });
            return;
        }

        const bmi = calculateBmi(formData.weight, formData.height);

        const payload = {
            patient_id: formData.patient_id,
            checkup_date: formData.checkup_date,
            blood_pressure: {
                systolic: formData.blood_pressure.systolic
                    ? Number(formData.blood_pressure.systolic)
                    : null,
                diastolic: formData.blood_pressure.diastolic
                    ? Number(formData.blood_pressure.diastolic)
                    : null,
            },
            blood_sugar: formData.blood_sugar ? Number(formData.blood_sugar) : null,
            heart_rate: formData.heart_rate ? Number(formData.heart_rate) : null,
            weight: formData.weight ? Number(formData.weight) : null,
            height: formData.height ? Number(formData.height) : null,
            bmi: bmi ? Number(bmi) : null,
            notes: formData.notes || null,
            record_image: formData.record_image || null,
            recorded_by: user.id,
        };

        try {
            let res;
            if (recordId) {
                res = await fetch(`${API_URL}/health_records/${recordId}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                res = await fetch(`${API_URL}/health_records`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }

            if (!res.ok) throw new Error('บันทึกข้อมูลไม่สำเร็จ');

            if (!recordId) {
                const nextDate = new Date();
                nextDate.setDate(nextDate.getDate() + 7);

                setVisitData(v => ({
                    ...v,
                    visit_date: nextDate.toISOString().split('T')[0]
                }));

                setOpenVisitDialog(true);
            } else {
                navigate(`/patients/${formData.patient_id}/health-records`);
            }

        } catch (err) {
            setStatus({ type: 'error', message: err.message });
        }
    };

    /* ---------- Visit actions ---------- */
    const handleConfirmVisit = async () => {
        const payload = {
            patient_id: formData.patient_id,
            visit_date: visitData.visit_date,
            visit_time: visitData.visit_time,
            location: visitData.location,
            service: visitData.service,
            provider_name: user.name,
            org_id: user.org_id || 1,
            created_at: new Date().toISOString(),
        };

        await fetch(`${API_URL}/visits`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        navigate(`/patients/${formData.patient_id}/health-records`);
    };

    const handleSkipVisit = () => {
        navigate(`/patients/${formData.patient_id}/health-records`);
    };

    /* ---------- Render ---------- */
    return (
        <>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
                {recordId ? 'แก้ไขประวัติการตรวจสุขภาพ' : 'บันทึกประวัติการตรวจสุขภาพ'}
            </Typography>

            <Paper sx={{ p: 4 }}>
                <Box component="form" onSubmit={handleSubmit}>

                    <Typography variant="h6" fontWeight="bold">
                        ข้อมูลการตรวจ
                    </Typography>

                    <Alert severity="info" sx={{ mb: 2 }}>
                        ผู้บันทึกข้อมูล: <strong>{user.name}</strong>
                    </Alert>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Autocomplete
                                options={patients}
                                value={selectedPatient}
                                onChange={(_, newValue) => {
                                    if (presetPatientId || recordId) return;
                                    setSelectedPatient(newValue);
                                    setFormData(prev => ({
                                        ...prev,
                                        patient_id: newValue ? newValue.id : ''
                                    }));
                                }}
                                getOptionLabel={(option) =>
                                    `${option.citizen_id} - ${option.first_name} ${option.last_name}`
                                }
                                disabled={Boolean(presetPatientId) || Boolean(recordId)}
                                renderInput={(params) => (
                                    <TextField {...params} label="ผู้สูงอายุ" required />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                label="โรคประจำตัว"
                                value={selectedPatient ? (selectedPatient.chronic_disease_note && selectedPatient.chronic_disease_note !== "**" && selectedPatient.chronic_disease_note !== "No" ? selectedPatient.chronic_disease_note : "ไม่มี/ไม่ระบุ") : "-"}
                                fullWidth
                                disabled
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="กลุ่ม ADL"
                                value={adlGroup}
                                fullWidth
                                disabled
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                name="checkup_date"
                                label="วันที่ตรวจ"
                                type="date"
                                fullWidth
                                required
                                InputLabelProps={{ shrink: true }}
                                value={formData.checkup_date}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>

                    {/* ===== Section: ค่าตรวจร่างกาย ===== */}
                    <Typography variant="h6" sx={{ mt: 4 }} fontWeight="bold">
                        ค่าตรวจร่างกาย
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                name="systolic"
                                label="ความดัน (ตัวบน)"
                                type="number"
                                fullWidth
                                value={formData.blood_pressure.systolic}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                name="diastolic"
                                label="ความดัน (ตัวล่าง)"
                                type="number"
                                fullWidth
                                value={formData.blood_pressure.diastolic}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                name="blood_sugar"
                                label="น้ำตาลในเลือด (mg/dL)"
                                type="number"
                                fullWidth
                                value={formData.blood_sugar}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                name="heart_rate"
                                label="อัตราการเต้นหัวใจ"
                                type="number"
                                fullWidth
                                value={formData.heart_rate}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                name="weight"
                                label="น้ำหนัก (kg)"
                                type="number"
                                fullWidth
                                value={formData.weight}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                name="height"
                                label="ส่วนสูง (cm)"
                                type="number"
                                fullWidth
                                value={formData.height}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                name="notes"
                                label="บันทึกเพิ่มเติม"
                                multiline
                                rows={3}
                                fullWidth
                                value={formData.notes}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CameraAltIcon />}
                            >
                                ถ่ายภาพ/แนบรูป (บาดแผล, สภาพแวดล้อม ฯลฯ)
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    hidden
                                    onChange={handleImageUpload}
                                />
                            </Button>
                            {formData.record_image && (
                                <Box sx={{ mt: 2, maxWidth: 300 }}>
                                    <img
                                        src={formData.record_image}
                                        alt="Record Preview"
                                        style={{ width: '100%', height: 'auto', borderRadius: 8 }}
                                    />
                                    <Box sx={{ mt: 1 }}>
                                        <Button size="small" color="error" onClick={() => setFormData(prev => ({ ...prev, record_image: '' }))}>
                                            ลบรูป
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Grid>

                    </Grid>

                
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button variant="outlined" onClick={() => navigate(formData.patient_id ? `/patients/${formData.patient_id}/health-records` : '/patients')}>
                            ยกเลิก
                        </Button>

                        <Button type="submit" variant="contained" size="large">
                            {recordId ? 'บันทึกการแก้ไข' : 'บันทึกข้อมูล'}
                        </Button>
                    </Box>

                    {status.message && (
                        <Alert severity={status.type} sx={{ mt: 2 }}>
                            {status.message}
                        </Alert>
                    )}
                </Box>
            </Paper>

            {/* ===== STEP 2 : Visit Dialog (เหมือนเดิม) ===== */}
            <Dialog open={openVisitDialog} maxWidth="sm" fullWidth>
                <DialogTitle>นัดติดตามครั้งถัดไป</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 6 }}>
                            <TextField
                                label="วันที่นัด"
                                type="date"
                                fullWidth
                                value={visitData.visit_date}
                                onChange={e =>
                                    setVisitData(v => ({ ...v, visit_date: e.target.value }))
                                }
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField
                                label="เวลา"
                                type="time"
                                fullWidth
                                value={visitData.visit_time}
                                onChange={e =>
                                    setVisitData(v => ({ ...v, visit_time: e.target.value }))
                                }
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="บริการ"
                                fullWidth
                                value={visitData.service}
                                onChange={e =>
                                    setVisitData(v => ({ ...v, service: e.target.value }))
                                }
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSkipVisit}>ยังไม่ต้องนัด</Button>
                    <Button variant="contained" onClick={handleConfirmVisit}>
                        ยืนยันนัด
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default RecordForm;